<template>
  <div class="workshop-materials-page">
    <Navbar />
    <PageBanner :title="t('navbar.workshopMaterials')" />

    <Breadcrumb :items="breadcrumbItems" />

    <!-- 官方教材說明 (滿版背景) -->
    <div class="materials-intro-wrapper">
      <div class="materials-intro-container">
        <h3 class="intro-title">官方教材說明</h3>
        <p class="intro-desc">
          教材由管理單位統一維護，供合作學協會辦理標準工作坊與教育推廣使用。
        </p>
      </div>
      <div class="intro-cards-wrapper">
        <div class="intro-cards-container">
          <div class="intro-cards">
            <div class="intro-card">
              <h4 class="card-title">教材用途</h4>
              <hr class="card-divider" />
              <p class="card-desc">提供合作學協會辦理工作坊與講師授課使用。</p>
            </div>
            <div class="intro-card">
              <h4 class="card-title">教材維護</h4>
              <hr class="card-divider" />
              <p class="card-desc">
                教材由管理單位統一上傳、更新與維護，確保各場工作坊使用一致版本。
              </p>
            </div>
            <div class="intro-card">
              <h4 class="card-title">取得方式</h4>
              <hr class="card-divider" />
              <p class="card-desc">
                授權承辦人請依通知信內容下載工作坊官方教材。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <h3 class="section-title">教材項目</h3>

      <!-- Scroll Hint on Mobile -->
      <div v-if="isScrollable" class="scroll-hint desktop-only">
        <svg
          class="scroll-hint-icon"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
        <span>{{ t("common.scrollHint") }}</span>
      </div>

      <div
        class="materials-content desktop-only"
        ref="contentRef"
        :data-can-scroll-right="canScrollRight ? 'true' : 'false'"
      >
        <table class="materials-table">
          <thead>
            <tr>
              <th class="col-item">項目</th>
              <th class="col-desc">說明</th>
              <th class="col-release">版本資訊</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="3">載入中...</td>
            </tr>
            <tr v-else-if="errorMessage">
              <td colspan="3">{{ errorMessage }}</td>
            </tr>
            <tr v-else-if="materials.length === 0">
              <td colspan="3">尚無資料</td>
            </tr>
            <tr v-else v-for="item in materials" :key="item.id">
              <td>{{ item.title }}</td>
              <td>{{ item.description }}</td>
              <td>
                <div class="version-text">
                  <div class="release-version">
                    {{ item.release_version || "V 1.0" }}
                  </div>
                  <div v-if="getMaterialDate(item)" class="published-at">
                    {{ getMaterialDate(item) }}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile loading/error/empty states -->
      <div class="mobile-only mobile-state-placeholder" v-if="isLoading || errorMessage || materials.length === 0">
        <div v-if="isLoading">載入中...</div>
        <div v-else-if="errorMessage">{{ errorMessage }}</div>
        <div v-else-if="materials.length === 0">尚無資料</div>
      </div>

      <!-- Mobile Cards View -->
      <div class="materials-cards mobile-only" v-else>
        <MobileCard
          v-for="item in materials"
          :key="item.id"
          :title="item.title"
          :rows="[
            { label: '說明', value: item.description, key: 'description' },
            { label: '發布版本', key: 'release' }
          ]"
        >
          <template #release>
            <div class="version-text">
              <div class="release-version">
                {{ item.release_version || "V 1.0" }}
              </div>
              <div v-if="getMaterialDate(item)" class="published-at">
                {{ getMaterialDate(item) }}
              </div>
            </div>
          </template>
        </MobileCard>
      </div>
    </div>
    <FooterSection />
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { message } from "ant-design-vue";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import { contentAPI, API_BASE_URL } from "@/services/api";
import MobileCard from "@/components/MobileCard.vue";

const { t } = useI18n();

const STANDARD = "loinc";

const isLoggedIn = ref(!!localStorage.getItem("ms_platform_token"));

const downloadMaterial = async (id, title, filePathOrUrl) => {
  const hideLoading = message.loading("正在準備下載教材...", 0);
  try {
    const res = await contentAPI.downloadWorkshopMaterial(id);
    const contentType = res.headers['content-type'] || 'application/octet-stream';
    const blob = new Blob([res.data], { type: contentType });

    // Extract file extension
    const ext = filePathOrUrl ? filePathOrUrl.split('.').pop().split('?')[0] : 'pdf';
    const filename = `${title}.${ext}`;

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);

    hideLoading();
    message.success("教材下載成功");
  } catch (error) {
    hideLoading();
    console.error("下載教材失敗", error);
    message.error("下載失敗，請稍後再試");
  }
};

const materials = ref([]);
const isLoading = ref(true);
const errorMessage = ref("");

const getMaterialDate = (item) => {
  const dateStr = item?.published_at || item?.created_at;
  if (!dateStr) return "";
  const datePart = String(dateStr).split("T")[0].split(" ")[0];
  return datePart.replace(/-/g, ".");
};

const contentRef = ref(null);
const canScrollRight = ref(false);
const isScrollable = ref(false);
let resizeObserver = null;

function updateScrollState() {
  const el = contentRef.value;
  if (!el) return;
  // 還能往右捲動時，於凍結第一欄右側顯示陰影提示使用者可向右滾動
  canScrollRight.value = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
  isScrollable.value = el.scrollWidth > el.clientWidth;
}

const breadcrumbItems = [
  { label: t("navbar.home"), path: "/" },
  { label: t("navbar.training") },
  { label: t("navbar.workshopMaterials") },
];

onMounted(async () => {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    const res = await contentAPI.workshopMaterials();
    materials.value = Array.isArray(res?.data) ? res.data : [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || "系統發生錯誤，請稍後再試";
  } finally {
    isLoading.value = false;
  }

  // 表格渲染完成後再判斷是否需要於凍結第一欄右側顯示陰影
  await nextTick();
  updateScrollState();

  const el = contentRef.value;
  if (el) {
    el.addEventListener("scroll", updateScrollState);
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);
    }
  }
  window.addEventListener("resize", updateScrollState);
});

onBeforeUnmount(() => {
  const el = contentRef.value;
  if (el) {
    el.removeEventListener("scroll", updateScrollState);
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  window.removeEventListener("resize", updateScrollState);
});
</script>

<style scoped lang="scss">
.workshop-materials-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 40px 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.materials-intro-wrapper {
  margin-bottom: 40px;
}

.materials-intro-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 20px 0;

  .intro-title {
    font-size: 32px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 16px;
  }

  .intro-desc {
    font-size: 20px;
    color: #333;
    margin-bottom: 32px;
  }
}

.intro-cards-wrapper {
  background-color: #f9fafb;
  width: 100%;
  padding: 32px 0;
}

.intro-cards-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
}

.intro-cards {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  .intro-card {
    flex: 1;
    min-width: 280px;

    .card-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--color-primary);
      margin-bottom: 12px;
    }

    .card-divider {
      border: none;
      height: 2px;
      background-color: var(--color-primary);
      margin-bottom: 16px;
      width: 100%;
    }

    .card-desc {
      font-size: 20px;
      color: #333;
      line-height: 1.6;
    }
  }
}

.section-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 24px;
}

.materials-content {
  background-color: #fff;
  border-radius: 8px;
  overflow-x: auto;
  border: 1px solid #eaeaea;
}

.materials-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.materials-table th,
.materials-table td {
  padding: 12px 20px;
  border-bottom: 1px solid #eaeaea;
}

.materials-table th {
  background-color: #f8f9fa;
  color: #333;
  font-weight: 600;
  font-size: 14px;
}

.materials-table td {
  color: #333;
  font-size: 16px;
  vertical-align: middle;
}

.materials-table tbody tr:last-child td {
  border-bottom: none;
}

.col-item {
  width: 30%;
}

.col-desc {
  width: 50%;
}

.col-release {
  width: 20%;
}

.version-link {
  display: inline-flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  .release-version {
    font-size: 16px;
    color: #333;
    font-weight: 500;
  }

  .published-at {
    font-size: 14px;
    color: #999;
    margin-top: 4px;
  }

  &:hover {
    .release-version {
      color: var(--color-primary);
      text-decoration: underline;
    }
  }
}

.version-text {
  display: inline-flex;
  flex-direction: column;

  .release-version {
    font-size: 16px;
    color: #333;
    font-weight: 500;
  }

  .published-at {
    font-size: 14px;
    color: #999;
    margin-top: 4px;
  }
}

@media (max-width: 768px) {
  .materials-table th,
  .materials-table td {
    padding: 16px 12px;
  }

  .materials-table {
    min-width: 650px;
  }

  .materials-table th:first-child,
  .materials-table td:first-child {
    position: sticky;
    left: 0;
    z-index: 10;
  }

  .materials-table th:first-child {
    background-color: #f8f9fa;
  }

  .materials-table td:first-child {
    background-color: #fff;
  }

  .materials-content[data-can-scroll-right="true"]
    .materials-table
    th:first-child::after,
  .materials-content[data-can-scroll-right="true"]
    .materials-table
    td:first-child::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0px;
    bottom: 0;
    width: 5px;
    pointer-events: none;
    box-shadow: 2px 0px 5px rgba(0, 0, 0, 0.08);
  }

  .col-item {
    width: 150px;
    min-width: 150px;
  }
  .col-desc {
    width: 220px;
    min-width: 220px;
  }
  .col-release {
    width: 120px;
    min-width: 120px;
  }
  .col-link {
    width: 160px;
    min-width: 160px;
  }

  .download-btn {
    width: auto;
    padding: 8px 16px;
  }
}

.scroll-hint {
  display: none;
}

@media (max-width: 768px) {
  .scroll-hint {
    display: inline-flex;
    align-items: center;
    background: rgba(0, 121, 187, 0.08);
    color: #0079bb;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    gap: 8px;
    animation: pulse-bg 2s infinite;

    .scroll-hint-icon {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      animation: bounce-right 1.5s infinite;
    }
  }
}

@keyframes bounce-right {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(4px);
  }
}

@keyframes pulse-bg {
  0%,
  100% {
    background-color: rgba(0, 121, 187, 0.08);
  }
  50% {
    background-color: rgba(0, 121, 187, 0.16);
  }
}

.desktop-only {
  display: block;
}
.mobile-only {
  display: none !important;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }
  .mobile-only {
    display: block !important;
  }
}

.mobile-state-placeholder {
  text-align: center;
  padding: 40px 20px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #eaeaea;
  color: #666;
  font-size: 16px;
}

.materials-cards {
  display: flex;
  flex-direction: column;
  width: 100%;

  .version-link, .version-text {
    display: inline-flex;
    flex-direction: column;
    
    .release-version {
      font-size: 15px;
      font-weight: 500;
      color: #333;
    }

    .published-at {
      font-size: 13px;
      color: #999;
      margin-top: 2px;
    }
  }

  .version-link:hover {
    .release-version {
      color: var(--color-primary);
      text-decoration: underline;
    }
  }
}
</style>
